import { apiFetch } from "../apiFetch";
import {
  loadStudioPreviewCenterState,
  type StudioLaunchControlState,
  type StudioPreviewCenterState,
  type StudioVoicePreviewLane,
} from "./studioPreviewCenter";

export const STUDIO_DEPLOYMENT_STATES = [
  "draft",
  "preview_ready",
  "canary_blocked",
  "canary_ready",
  "deploy_blocked",
] as const;

export type StudioDeploymentState =
  (typeof STUDIO_DEPLOYMENT_STATES)[number];

export type StudioVoiceRolloutReadiness = {
  checkedAt?: string;
  version: string;
  target: "future_a21_canary";
  enabled: boolean;
  canaryEligible: boolean;
  status: "blocked" | "ready_for_a21_canary";
  blockedReasons: string[];
  manifest: {
    approvalStatus:
      | "pending_owner_approval"
      | "approved_prep_only"
      | "approved_for_a21_canary";
    allowedChannels: string[];
    blockedChannels: string[];
    rejectedRequestedChannels: string[];
    personaVersion: string;
    previewAdapterVersion: string;
    qualityGateVersion: string;
    qualityGateStatus: "pass" | "fail";
    activationPolicy: "manifest_only_no_live_activation";
  };
  previewReadiness: {
    ready: boolean;
    status?: string;
    blockedReasons: string[];
  };
  qualityGate: {
    version: string;
    status: "pass" | "fail";
    summary?: {
      sampleCount?: number;
      passed?: number;
      failed?: number;
    };
  };
  isolation: {
    clinicLiveActivation: false;
    twilioProductionRoutingUntouched: true;
    previewOnlyUntilA21: true;
  };
};

export type StudioDeploymentSnapshot = {
  access: StudioPreviewCenterState["access"];
  voiceLane: StudioVoicePreviewLane | null;
  launchControl: StudioLaunchControlState | null;
  rolloutReadiness: StudioVoiceRolloutReadiness | null;
  errors: {
    voiceReadiness: string | null;
    deploymentTruth: string | null;
    rolloutReadiness: string | null;
  };
};

export type StudioDeploymentBlockerCategory =
  | "access"
  | "preview_config"
  | "missing_env"
  | "blocked_channel"
  | "approval"
  | "quality"
  | "launch_constraint"
  | "rollout_policy"
  | "data";

export type StudioDeploymentBlocker = {
  code: string;
  category: StudioDeploymentBlockerCategory;
  label: string;
  detail: string;
};

export type StudioDeploymentStatusCardTone = "ready" | "review" | "blocked";

export type StudioDeploymentStatusCard = {
  label: string;
  value: string;
  detail: string;
  tone: StudioDeploymentStatusCardTone;
};

export type StudioDeploymentDecision = {
  checkedAt: string | null;
  state: StudioDeploymentState;
  stateLabel: string;
  headline: string;
  summary: string;
  blockers: StudioDeploymentBlocker[];
  nextActions: string[];
  statusCards: StudioDeploymentStatusCard[];
};

function isUnauthorized(response: Response | null | undefined) {
  return response?.status === 401 || response?.status === 403;
}

async function safeJson<T>(response: Response | null | undefined, fallback: T) {
  if (!response) return fallback;

  try {
    return (await response.json()) as T;
  } catch {
    return fallback;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function normalizeText(value: string | undefined | null, fallback: string) {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function formatApprovalStatus(
  status:
    | "pending_owner_approval"
    | "approved_prep_only"
    | "approved_for_a21_canary"
) {
  if (status === "approved_for_a21_canary") return "Approved for A21 canary";
  if (status === "approved_prep_only") return "Approved for prep only";
  return "Pending owner approval";
}

function toneForBoolean(value: boolean): StudioDeploymentStatusCardTone {
  return value ? "ready" : "blocked";
}

function addBlocker(
  blockers: StudioDeploymentBlocker[],
  blocker: StudioDeploymentBlocker
) {
  if (blockers.some((item) => item.code === blocker.code)) {
    return;
  }

  blockers.push(blocker);
}

function mapPreviewBlockedReason(
  reason: string,
  voiceLane: StudioVoicePreviewLane | null
): StudioDeploymentBlocker | null {
  switch (reason) {
    case "preview_disabled":
      return {
        code: reason,
        category: "preview_config",
        label: "Preview lane disabled",
        detail:
          "Enable the internal preview lane first. Studio must stay in preview-only mode.",
      };
    case "persona_opt_in_required":
      return {
        code: reason,
        category: "preview_config",
        label: "No allowed scenario channel opted in",
        detail:
          "Opt the persona into an allowed scenario channel before treating this assistant as preview-ready.",
      };
    case "api_key_missing":
      return {
        code: reason,
        category: "missing_env",
        label: "ELEVENLABS_API_KEY missing",
        detail:
          "Set the ElevenLabs API key for preview-only rendering. Do not wire it into live clinic paths.",
      };
    case "voice_id_missing":
      return {
        code: reason,
        category: "missing_env",
        label: "ELEVENLABS_VOICE_ID missing",
        detail: `Configure ELEVENLABS_PREVIEW_VOICE_ID or ELEVENLABS_VOICE_ID for ${voiceLane?.personaSelection.personaName || "the current preview persona"}.`,
      };
    case "model_id_missing":
      return {
        code: reason,
        category: "missing_env",
        label: "ELEVENLABS_MODEL_ID missing",
        detail:
          "Set ELEVENLABS_PREVIEW_MODEL_ID or ELEVENLABS_MODEL_ID before moving past draft state.",
      };
    default:
      return null;
  }
}

function mapRolloutBlockedReason(
  reason: string,
  rolloutReadiness: StudioVoiceRolloutReadiness
): StudioDeploymentBlocker | null {
  switch (reason) {
    case "rollout_disabled":
      return {
        code: reason,
        category: "rollout_policy",
        label: "Rollout manifest disabled",
        detail:
          "The A21 canary switchboard remains off. Keep working in preview until explicit rollout controls are enabled.",
      };
    case "approval_pending":
      return {
        code: reason,
        category: "approval",
        label: "Approval not yet at A21 canary",
        detail: `Current approval status: ${formatApprovalStatus(rolloutReadiness.manifest.approvalStatus)}.`,
      };
    case "quality_gate_failed":
      return {
        code: reason,
        category: "quality",
        label: "Humanization quality gate failed",
        detail:
          "The deterministic humanization harness must return pass before future canary work can proceed.",
      };
    case "preview_provider_not_ready":
      return {
        code: reason,
        category: "preview_config",
        label: "Preview provider not ready",
        detail:
          "The ElevenLabs preview adapter must be ready before the rollout manifest can advance.",
      };
    case "unsafe_live_channel_requested":
      return {
        code: reason,
        category: "blocked_channel",
        label: "Blocked live channels were requested",
        detail: `Rejected channels: ${rolloutReadiness.manifest.rejectedRequestedChannels.join(", ") || "unknown"}.`,
      };
    case "no_allowed_channels":
      return {
        code: reason,
        category: "preview_config",
        label: "No allowed scenario channels",
        detail:
          "Request only approved scenario channels before discussing canary readiness.",
      };
    case "manifest_invalid":
      return {
        code: reason,
        category: "rollout_policy",
        label: "Rollout manifest invalid",
        detail:
          "The rollout manifest no longer matches the current preview-only policy and must be corrected first.",
      };
    default:
      return null;
  }
}

function parseRolloutReadiness(
  payload: unknown
): StudioVoiceRolloutReadiness | null {
  if (!isRecord(payload)) {
    return null;
  }

  if (
    typeof payload.enabled !== "boolean" ||
    typeof payload.canaryEligible !== "boolean" ||
    (payload.status !== "blocked" && payload.status !== "ready_for_a21_canary")
  ) {
    return null;
  }

  const manifest = isRecord(payload.manifest) ? payload.manifest : null;
  const previewReadiness = isRecord(payload.previewReadiness)
    ? payload.previewReadiness
    : null;
  const qualityGate = isRecord(payload.qualityGate) ? payload.qualityGate : null;
  const isolation = isRecord(payload.isolation) ? payload.isolation : null;

  if (!manifest || !previewReadiness || !qualityGate || !isolation) {
    return null;
  }

  if (
    typeof manifest.approvalStatus !== "string" ||
    !Array.isArray(manifest.allowedChannels) ||
    !Array.isArray(manifest.blockedChannels) ||
    !Array.isArray(manifest.rejectedRequestedChannels) ||
    typeof qualityGate.status !== "string" ||
    typeof previewReadiness.ready !== "boolean" ||
    !Array.isArray(previewReadiness.blockedReasons)
  ) {
    return null;
  }

  return {
    checkedAt:
      typeof payload.checkedAt === "string" ? payload.checkedAt : undefined,
    version: normalizeText(
      typeof payload.version === "string" ? payload.version : null,
      "unknown"
    ),
    target: "future_a21_canary",
    enabled: payload.enabled,
    canaryEligible: payload.canaryEligible,
    status: payload.status,
    blockedReasons: Array.isArray(payload.blockedReasons)
      ? payload.blockedReasons.filter((item): item is string => typeof item === "string")
      : [],
    manifest: {
      approvalStatus:
        manifest.approvalStatus === "approved_for_a21_canary" ||
        manifest.approvalStatus === "approved_prep_only"
          ? manifest.approvalStatus
          : "pending_owner_approval",
      allowedChannels: manifest.allowedChannels.filter(
        (item): item is string => typeof item === "string"
      ),
      blockedChannels: manifest.blockedChannels.filter(
        (item): item is string => typeof item === "string"
      ),
      rejectedRequestedChannels: manifest.rejectedRequestedChannels.filter(
        (item): item is string => typeof item === "string"
      ),
      personaVersion: normalizeText(
        typeof manifest.personaVersion === "string" ? manifest.personaVersion : null,
        "unknown"
      ),
      previewAdapterVersion: normalizeText(
        typeof manifest.previewAdapterVersion === "string"
          ? manifest.previewAdapterVersion
          : null,
        "unknown"
      ),
      qualityGateVersion: normalizeText(
        typeof manifest.qualityGateVersion === "string"
          ? manifest.qualityGateVersion
          : null,
        "unknown"
      ),
      qualityGateStatus:
        qualityGate.status === "pass" ? "pass" : "fail",
      activationPolicy: "manifest_only_no_live_activation",
    },
    previewReadiness: {
      ready: previewReadiness.ready,
      status:
        typeof previewReadiness.status === "string"
          ? previewReadiness.status
          : undefined,
      blockedReasons: previewReadiness.blockedReasons.filter(
        (item): item is string => typeof item === "string"
      ),
    },
    qualityGate: {
      version: normalizeText(
        typeof qualityGate.version === "string" ? qualityGate.version : null,
        "unknown"
      ),
      status: qualityGate.status === "pass" ? "pass" : "fail",
      summary: isRecord(qualityGate.summary)
        ? {
            sampleCount:
              typeof qualityGate.summary.sampleCount === "number"
                ? qualityGate.summary.sampleCount
                : undefined,
            passed:
              typeof qualityGate.summary.passed === "number"
                ? qualityGate.summary.passed
                : undefined,
            failed:
              typeof qualityGate.summary.failed === "number"
                ? qualityGate.summary.failed
                : undefined,
          }
        : undefined,
    },
    isolation: {
      clinicLiveActivation: false,
      twilioProductionRoutingUntouched: true,
      previewOnlyUntilA21: true,
    },
  };
}

export async function loadStudioDeploymentSnapshot(): Promise<StudioDeploymentSnapshot> {
  const [previewCenter, rolloutResponse] = await Promise.all([
    loadStudioPreviewCenterState(),
    apiFetch("/api/internal/readiness/voice-rollout"),
  ]);

  let rolloutReadiness: StudioVoiceRolloutReadiness | null = null;
  let rolloutReadinessError: string | null = null;

  if (isUnauthorized(rolloutResponse) || previewCenter.access.status === "blocked") {
    return {
      access: {
        status: "blocked",
        reason: "auth_required",
      },
      voiceLane: previewCenter.voiceReadiness,
      launchControl: previewCenter.deploymentTruth,
      rolloutReadiness: null,
      errors: {
        voiceReadiness: previewCenter.errors.voiceReadiness,
        deploymentTruth: previewCenter.errors.deploymentTruth,
        rolloutReadiness: null,
      },
    };
  }

  if (!rolloutResponse?.ok) {
    rolloutReadinessError = "Voice rollout readiness unavailable.";
  } else {
    const payload = await safeJson(rolloutResponse, {});
    rolloutReadiness = parseRolloutReadiness(payload);
    if (!rolloutReadiness) {
      rolloutReadinessError = "Voice rollout readiness data malformed.";
    }
  }

  return {
    access: previewCenter.access,
    voiceLane: previewCenter.voiceReadiness,
    launchControl: previewCenter.deploymentTruth,
    rolloutReadiness,
    errors: {
      voiceReadiness: previewCenter.errors.voiceReadiness,
      deploymentTruth: previewCenter.errors.deploymentTruth,
      rolloutReadiness: rolloutReadinessError,
    },
  };
}

export function createUnavailableStudioDeploymentSnapshot(
  message = "Studio deployment readiness could not be loaded."
): StudioDeploymentSnapshot {
  return {
    access: {
      status: "ready",
      reason: null,
    },
    voiceLane: null,
    launchControl: null,
    rolloutReadiness: null,
    errors: {
      voiceReadiness: message,
      deploymentTruth: message,
      rolloutReadiness: message,
    },
  };
}

export function evaluateStudioDeploymentState(
  snapshot: StudioDeploymentSnapshot
): StudioDeploymentDecision {
  const blockers: StudioDeploymentBlocker[] = [];
  const checkedAt =
    snapshot.rolloutReadiness?.checkedAt ||
    snapshot.launchControl?.checkedAt ||
    snapshot.voiceLane?.checkedAt ||
    null;

  if (snapshot.access.status === "blocked") {
    addBlocker(blockers, {
      code: "auth_required",
      category: "access",
      label: "Operator access required",
      detail:
        "Studio deployment choreography only renders current readiness for internal operator or admin viewers.",
    });
  }

  if (snapshot.errors.voiceReadiness) {
    addBlocker(blockers, {
      code: "voice_readiness_unavailable",
      category: "data",
      label: "Voice readiness data unavailable",
      detail: snapshot.errors.voiceReadiness,
    });
  }

  if (snapshot.errors.deploymentTruth) {
    addBlocker(blockers, {
      code: "launch_control_unavailable",
      category: "data",
      label: "Launch control data unavailable",
      detail: snapshot.errors.deploymentTruth,
    });
  }

  if (snapshot.errors.rolloutReadiness) {
    addBlocker(blockers, {
      code: "rollout_readiness_unavailable",
      category: "data",
      label: "Rollout readiness data unavailable",
      detail: snapshot.errors.rolloutReadiness,
    });
  }

  if (!snapshot.voiceLane && snapshot.access.status === "ready") {
    addBlocker(blockers, {
      code: "voice_lane_missing",
      category: "data",
      label: "Voice preview lane missing",
      detail:
        "Studio could not load the preview-lane snapshot, so deployment gating cannot be trusted yet.",
    });
  }

  if (!snapshot.launchControl && snapshot.access.status === "ready") {
    addBlocker(blockers, {
      code: "launch_control_missing",
      category: "data",
      label: "Launch control snapshot missing",
      detail:
        "Studio could not load the current launch-control truth, so deployment guidance remains blocked.",
    });
  }

  if (!snapshot.rolloutReadiness && snapshot.access.status === "ready") {
    addBlocker(blockers, {
      code: "rollout_snapshot_missing",
      category: "data",
      label: "Rollout snapshot missing",
      detail:
        "Studio could not load the current A21 canary switchboard state.",
    });
  }

  for (const reason of snapshot.voiceLane?.blockedReasons || []) {
    const blocker = mapPreviewBlockedReason(reason, snapshot.voiceLane);
    if (blocker) addBlocker(blockers, blocker);
  }

  if (snapshot.voiceLane && snapshot.voiceLane.allowedChannels.length === 0) {
    addBlocker(blockers, {
      code: "preview_allowed_channels_missing",
      category: "preview_config",
      label: "No allowed scenario channels configured",
      detail:
        "Keep the lane inside approved scenario channels before advancing Studio readiness.",
    });
  }

  if (snapshot.launchControl && !snapshot.launchControl.overallReady) {
    addBlocker(blockers, {
      code: "launch_constraints_unresolved",
      category: "launch_constraint",
      label: "Launch constraints unresolved",
      detail:
        snapshot.launchControl.nextActions
          ?.map((item) => item.action || item.category || "")
          .filter(Boolean)
          .join(" ")
          || "Review launch-control blockers before discussing canary activation.",
    });
  }

  const rolloutReadiness = snapshot.rolloutReadiness;
  if (rolloutReadiness) {
    for (const reason of rolloutReadiness.blockedReasons) {
      const blocker = mapRolloutBlockedReason(reason, rolloutReadiness);
      if (blocker) addBlocker(blockers, blocker);
    }

    const blockedRequestedChannels = rolloutReadiness.manifest.rejectedRequestedChannels.filter(
      (channel) => rolloutReadiness.manifest.blockedChannels.includes(channel)
    );

    if (blockedRequestedChannels.length > 0) {
      addBlocker(blockers, {
        code: "blocked_live_channels_requested",
        category: "blocked_channel",
        label: "Launch-critical channels remain blocked",
        detail: `Rejected live channels: ${blockedRequestedChannels.join(", ")}.`,
      });
    }
  }

  const hardBlocked =
    snapshot.access.status === "blocked" ||
    Boolean(snapshot.errors.voiceReadiness) ||
    Boolean(snapshot.errors.deploymentTruth) ||
    Boolean(snapshot.errors.rolloutReadiness) ||
    !snapshot.voiceLane ||
    !snapshot.launchControl ||
    !rolloutReadiness ||
    blockers.some(
      (blocker) =>
        blocker.category === "blocked_channel" || blocker.code === "manifest_invalid"
    );

  const previewReady = Boolean(snapshot.voiceLane?.ready);
  const previewDraft =
    !previewReady ||
    blockers.some(
      (blocker) =>
        blocker.category === "missing_env" || blocker.category === "preview_config"
    );

  const canaryIntent = Boolean(
    rolloutReadiness &&
      (rolloutReadiness.enabled ||
        rolloutReadiness.manifest.approvalStatus === "approved_for_a21_canary" ||
        rolloutReadiness.manifest.allowedChannels.length > 0 ||
        rolloutReadiness.manifest.rejectedRequestedChannels.length > 0)
  );

  let state: StudioDeploymentState = "preview_ready";

  if (hardBlocked) {
    state = "deploy_blocked";
  } else if (previewDraft) {
    state = "draft";
  } else if (
    rolloutReadiness &&
    rolloutReadiness.canaryEligible &&
    snapshot.launchControl?.overallReady
  ) {
    state = "canary_ready";
  } else if (canaryIntent) {
    state = "canary_blocked";
  } else {
    state = "preview_ready";
  }

  const approvalValue = rolloutReadiness
    ? formatApprovalStatus(rolloutReadiness.manifest.approvalStatus)
    : "Unavailable";
  const launchValue = snapshot.launchControl
    ? snapshot.launchControl.overallReady
      ? "Launch constraints clear"
      : `${snapshot.launchControl.blockerCount} launch blocker${snapshot.launchControl.blockerCount === 1 ? "" : "s"}`
    : "Unavailable";
  const rolloutValue = rolloutReadiness
    ? rolloutReadiness.status === "ready_for_a21_canary"
      ? "A21 canary eligible"
      : rolloutReadiness.enabled
        ? "Canary request blocked"
        : "Prep-only rollout"
    : "Unavailable";

  const statusCards: StudioDeploymentStatusCard[] = [
    {
      label: "Current stage",
      value: state.replaceAll("_", "-"),
      detail:
        state === "draft"
          ? "The Studio draft still needs preview-only configuration."
          : state === "preview_ready"
            ? "Internal preview is ready while live activation remains gated."
            : state === "canary_blocked"
              ? "Canary intent exists, but blockers still prevent a safe handoff."
              : state === "canary_ready"
                ? "Canary inputs are aligned, but Studio still cannot flip live routing."
                : "A hard safety or data issue keeps deploy choreography blocked.",
      tone:
        state === "canary_ready"
          ? "ready"
          : state === "preview_ready"
            ? "review"
            : "blocked",
    },
    {
      label: "Preview lane",
      value: previewReady ? "Preview ready" : "Preview blocked",
      detail:
        snapshot.voiceLane?.allowedChannels.join(", ") ||
        "No allowed scenario channels loaded.",
      tone: toneForBoolean(previewReady),
    },
    {
      label: "Approval",
      value: approvalValue,
      detail:
        "Studio cannot self-approve A21 canary work. Approval remains explicit and external to this shell.",
      tone:
        rolloutReadiness?.manifest.approvalStatus === "approved_for_a21_canary"
          ? "ready"
          : "review",
    },
    {
      label: "Launch control",
      value: launchValue,
      detail:
        snapshot.launchControl?.nextActions
          ?.map((item) => item.action || item.category || "")
          .filter(Boolean)
          .join(" ")
          || "Launch-control truth stays visible and unchanged.",
      tone:
        snapshot.launchControl?.overallReady
          ? "ready"
          : snapshot.launchControl
            ? "blocked"
            : "blocked",
    },
    {
      label: "Rollout switchboard",
      value: rolloutValue,
      detail:
        rolloutReadiness?.manifest.activationPolicy ||
        "A21 canary switchboard unavailable.",
      tone:
        rolloutReadiness?.status === "ready_for_a21_canary"
          ? "ready"
          : rolloutReadiness?.enabled
            ? "blocked"
            : "review",
    },
  ];

  const nextActions =
    state === "draft"
      ? [
          "Finish preview-only ElevenLabs configuration and approved channel opt-in before moving past draft state.",
          "Keep all rendering inside the internal preview lane and away from live clinic traffic.",
        ]
      : state === "preview_ready"
        ? [
            "Use preview and sandbox rehearsal to validate persona, tone, and friction handling.",
            "If future canary work is desired, enable explicit rollout controls and wait for approval rather than changing live routing here.",
          ]
        : state === "canary_blocked"
          ? [
              "Resolve the listed canary blockers, then rerun the deployment snapshot.",
              "Keep Twilio production routing and webhook bindings untouched while the canary remains blocked.",
            ]
          : state === "canary_ready"
            ? [
                "Carry this snapshot into the future A21 canary review path. Studio remains prep-only.",
                "Do not activate inbound, outbound, or webhook changes from this shell.",
              ]
            : [
                "Clear the hard safety or data blockers before trusting Studio deployment guidance.",
                "Unsafe requested live channels, invalid rollout policy, or missing internal access must be resolved first.",
              ];

  return {
    checkedAt,
    state,
    stateLabel: state.replaceAll("_", "-"),
    headline:
      state === "draft"
        ? "Draft only"
        : state === "preview_ready"
          ? "Preview-ready"
          : state === "canary_blocked"
            ? "Canary blocked"
            : state === "canary_ready"
              ? "Canary-ready"
              : "Deploy blocked",
    summary:
      state === "draft"
        ? "Studio has a valid draft, but preview-only execution is not fully configured yet."
        : state === "preview_ready"
          ? "Preview checks are ready for internal use. Canary controls remain explicit and gated."
          : state === "canary_blocked"
            ? "Studio can evaluate a future canary, but deterministic blockers still prevent safe handoff."
            : state === "canary_ready"
              ? "Preview, quality, rollout, and launch-control inputs line up for future A21 canary review."
              : "A hard policy or data issue makes the current deploy snapshot unsafe to advance.",
    blockers,
    nextActions,
    statusCards,
  };
}
